import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Search, X, Star, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import LogoImage from '@/assets/images/Logo.png';
import { useMenu } from '@/context/MenuContext';
import { getMenuIcon, findFirstPath } from '@/lib/menuUtils';
import MenuNode from './MenuNode';

/**
 * LNB — 현재 모듈(GNB에서 선택된 최상위 메뉴)의 하위 메뉴 트리.
 *
 * 이전 버전과의 차이:
 *  - 2-depth 하드코딩 → MenuNode 재귀로 N-depth 지원
 *  - 메뉴명 문자열 분기('설정 관리' 등) 전면 제거
 *  - 즐겨찾기 / 메뉴검색 / 배지 슬롯 추가
 *  - 권한 필터링은 백엔드가 이미 수행하므로 프론트에서 중복 필터링하지 않음
 */
const Sidebar = ({ isOpen, isCollapsed, toggleSidebar, toggleCollapse, onOpenSearch }) => {
  const { modules, activeModule, favorites, isLoading } = useMenu();

  const currentModule = activeModule || modules[0] || null;

  // 모듈 자체 링크를 위에 따로 그리므로, 같은 경로를 가리키는 자식은 제외해 중복을 막는다.
  // (예: '대시보드' 모듈과 그 하위 '대시보드' 가 둘 다 /dashboard)
  const children = (currentModule?.children || [])
    .filter((node) => node.isDisplay)
    .filter((node) => !currentModule?.urlPath || node.urlPath !== currentModule.urlPath);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-chrome text-chrome-text transition-all duration-300 ease-in-out lg:static lg:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* 상단: 현재 모듈명 + 접기/닫기 */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-chrome-border px-4">
        {isCollapsed ? (
          <Link to="/dashboard" className="mx-auto text-lg font-black text-chrome-accent" title="대시보드">
            SC
          </Link>
        ) : (
          <>
            <span className="shrink-0 text-chrome-accent">{getMenuIcon(currentModule?.iconClass, 18)}</span>
            <span className="flex-1 truncate text-sm font-bold tracking-tight">
              {currentModule?.menuName || 'SMEs CRM'}
            </span>
          </>
        )}

        <button
          type="button"
          onClick={toggleCollapse}
          className="hidden shrink-0 rounded p-1 text-chrome-muted hover:bg-chrome-hover hover:text-chrome-strong lg:block"
          aria-label={isCollapsed ? '사이드바 펼치기' : '사이드바 접기'}
        >
          {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>

        <button
          type="button"
          onClick={toggleSidebar}
          className="shrink-0 text-chrome-muted hover:text-chrome-strong lg:hidden"
          aria-label="사이드바 닫기"
        >
          <X size={22} />
        </button>
      </div>

      {/* 메뉴 검색 진입 */}
      {!isCollapsed && (
        <div className="shrink-0 px-3 pt-3">
          <button
            type="button"
            onClick={onOpenSearch}
            className="flex w-full items-center gap-2 rounded-md bg-chrome-hover px-3 py-2 text-left text-sm text-chrome-muted transition-colors hover:bg-chrome-active hover:text-chrome-strong"
          >
            <Search size={15} className="shrink-0" />
            <span className="flex-1">메뉴 검색</span>
            <kbd className="shrink-0 rounded border border-chrome-border px-1 text-[10px] text-chrome-muted">Ctrl K</kbd>
          </button>
        </div>
      )}

      <nav className="scrollbar-hide flex-1 space-y-1 overflow-y-auto px-3 py-3">
        {/* 즐겨찾기 */}
        {!isCollapsed && favorites.length > 0 && (
          <div className="mb-3 border-b border-chrome-border pb-3">
            <p className="mb-1 flex items-center gap-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-chrome-muted">
              <Star size={11} fill="currentColor" /> 즐겨찾기
            </p>
            {favorites.map((favorite) => (
              <NavLink
                key={favorite.menuId}
                to={favorite.urlPath || '/dashboard'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'menu-item-active'
                      : 'text-chrome-text hover:bg-chrome-hover hover:text-chrome-strong'
                  )
                }
              >
                <span className="shrink-0 text-chrome-muted">{getMenuIcon(favorite.iconClass, 15)}</span>
                <span className="truncate">{favorite.menuName}</span>
              </NavLink>
            ))}
          </div>
        )}

        {/* 접힘 모드: 모듈 아이콘만 세로 배치 */}
        {isCollapsed ? (
          modules.map((module) => (
            <Link
              key={module.menuId}
              to={findFirstPath(module) || '/dashboard'}
              title={module.menuName}
              className={cn(
                'flex justify-center rounded-md p-3 transition-colors',
                activeModule?.menuId === module.menuId
                  ? 'bg-chrome-active text-chrome-active-text ring-1 ring-chrome-accent/40'
                  : 'text-chrome-text hover:bg-chrome-hover hover:text-chrome-strong'
              )}
            >
              {getMenuIcon(module.iconClass, 20)}
            </Link>
          ))
        ) : (
          <>
            {isLoading && (
              <p className="px-3 py-4 text-xs text-chrome-muted">메뉴를 불러오는 중…</p>
            )}

            {/* 모듈 자체에 화면이 있으면(예: 전자결재 대시보드) 맨 위에 노출한다.
                화면이 없는 그룹 메뉴는 DB 에서 url_path 가 NULL 이라 자동으로 걸러진다. */}
            {!isLoading && currentModule?.urlPath && (
              <NavLink
                to={currentModule.urlPath}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'menu-item-active'
                      : 'text-chrome-text hover:bg-chrome-hover hover:text-chrome-strong'
                  )
                }
              >
                <span className="shrink-0 text-chrome-muted">{getMenuIcon(currentModule.iconClass, 18)}</span>
                <span className="truncate">{currentModule.menuName}</span>
              </NavLink>
            )}

            {!isLoading &&
              children.map((node) => <MenuNode key={node.menuId} node={node} depth={0} />)}

            {!isLoading && !currentModule && (
              <p className="px-3 py-4 text-xs text-chrome-muted">표시할 메뉴가 없습니다.</p>
            )}
          </>
        )}
      </nav>

      {/* 하단 고정 로고 */}
      <div className="shrink-0 border-t border-chrome-border bg-chrome-2 p-4">
        <div className="flex w-full items-center justify-center opacity-35 transition-opacity hover:opacity-100">
          {isCollapsed ? (
            getMenuIcon('Building', 22)
          ) : (
            <img src={LogoImage} alt="WORK&CRM" className="chrome-logo h-8 w-full object-contain" />
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
